import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/dashboardService';
import { DashboardData, DashboardFilters, RevenueStats, BusDashboardStats, BusRevenueStats } from '../interfaces/Dashboard';
import { DefaultLoader } from '../components';
import { 
  Users, 
  CreditCard, 
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  Filter,
  Settings as SettingsIcon,
  Bus,
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null);
  const [busStats, setBusStats] = useState<BusDashboardStats | null>(null);
  const [busRevenueStats, setBusRevenueStats] = useState<BusRevenueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showBusFilters, setShowBusFilters] = useState(false);
  const [filters, setFilters] = useState<DashboardFilters>({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    status: 'completed'
  });
  const [busFilters, setBusFilters] = useState<DashboardFilters>({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [data, revenue, bus, busRevenue] = await Promise.all([
          dashboardService.getDashboardData(),
          dashboardService.getRevenueWithFilters(filters),
          dashboardService.getBusStats(),
          dashboardService.getBusRevenueByDateRange(busFilters.startDate!, busFilters.endDate!),
        ]);
        setDashboardData(data);
        setRevenueStats(revenue);
        setBusStats(bus);
        setBusRevenueStats(busRevenue);
        setError(null);
      } catch (err) {
        console.error('Erro ao buscar dados do dashboard:', err);
        setError('Erro ao carregar dados do dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [filters]);

  const handleFilterChange = (newFilters: Partial<DashboardFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const fetchRevenueData = async () => {
    try {
      const revenue = await dashboardService.getRevenueWithFilters(filters);
      setRevenueStats(revenue);
    } catch (err) {
      console.error('Erro ao buscar dados de receita:', err);
    }
  };

  const handleBusFilterChange = (newFilters: Partial<DashboardFilters>) => {
    setBusFilters(prev => ({ ...prev, ...newFilters }));
  };

  const fetchBusRevenueData = async () => {
    try {
      const revenue = await dashboardService.getBusRevenueByDateRange(
        busFilters.startDate!,
        busFilters.endDate!,
      );
      setBusRevenueStats(revenue);
    } catch (err) {
      console.error('Erro ao buscar dados de receita de ônibus:', err);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStatsConfig = () => [
    {
      title: 'Passagens Aéreas Pendentes',
      value: dashboardData?.stats.pendingTickets?.toString() || '0',
      icon: <Clock size={24} />,
      color: 'orange',
      roles: ['admin', 'emissor', 'reader'],
    },
    {
      title: 'Passagens Aéreas Emitidas',
      value: dashboardData?.stats.issuedTickets?.toString() || '0',
      icon: <CheckCircle size={24} />,
      color: 'green',
      roles: ['admin', 'emissor', 'reader'],
    },
    {
      title: 'Cancelamentos (Aéreo)',
      value: dashboardData?.stats.cancellations?.toString() || '0',
      icon: <XCircle size={24} />,
      color: 'red',
      roles: ['admin', 'emissor', 'reader'],
    },
    {
      title: 'Ônibus Aguard. Pagamento',
      value: busStats?.busPendingTickets?.toString() || '0',
      icon: <Bus size={24} />,
      color: 'orange',
      roles: ['admin', 'emissor', 'reader'],
    },
    {
      title: 'Ônibus Emitidos',
      value: busStats?.busIssuedTickets?.toString() || '0',
      icon: <Bus size={24} />,
      color: 'green',
      roles: ['admin', 'emissor', 'reader'],
    },
    {
      title: 'Cancelamentos (Ônibus)',
      value: busStats?.busCancellations?.toString() || '0',
      icon: <Bus size={24} />,
      color: 'red',
      roles: ['admin', 'emissor', 'reader'],
    },
    {
      title: 'Total de Usuários',
      value: dashboardData?.stats.totalUsers?.toString() || '0',
      icon: <Users size={24} />,
      color: 'blue',
      roles: ['admin'],
    },
    {
      title: `Receita Aérea (${revenueStats?.period || 'Mês Atual'})`,
      value: revenueStats?.totalRevenue ? formatCurrency(revenueStats.totalRevenue) : formatCurrency(dashboardData?.stats.monthlyRevenue || 0),
      icon: <DollarSign size={24} />,
      color: 'green',
      roles: ['admin', 'gestor'],
    },
    {
      title: `Receita Ônibus (${busRevenueStats?.period || 'Mês Atual'})`,
      value: busRevenueStats?.totalRevenue ? formatCurrency(busRevenueStats.totalRevenue) : formatCurrency(busStats?.busMonthlyRevenue || 0),
      icon: <DollarSign size={24} />,
      color: 'blue',
      roles: ['admin', 'gestor'],
    },
    {
      title: 'Markups Ativos',
      value: dashboardData?.stats.activeMarkups?.toString() || '0',
      icon: <TrendingUp size={24} />,
      color: 'purple',
      roles: ['admin', 'gestor'],
    },
  ];

  const getVisibleStats = () => {
    if (!role) return [];
    const stats = getStatsConfig();
    return stats.filter(stat => 
      stat.roles.some(requiredRole => role[requiredRole as keyof typeof role])
    );
  };

  const getColorClass = (color: string) => {
    const colorMap: { [key: string]: string } = {
      orange: 'var(--color-orange)',
      green: 'var(--color-green)',
      red: 'var(--color-red)',
      blue: 'var(--color-blue)',
      purple: '#8b5cf6',
    };
    return colorMap[color] || 'var(--color-dimgray)';
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'agora mesmo';
    if (diffInMinutes === 1) return 'há 1 minuto';
    if (diffInMinutes < 60) return `há ${diffInMinutes} minutos`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours === 1) return 'há 1 hora';
    if (diffInHours < 24) return `há ${diffInHours} horas`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'há 1 dia';
    return `há ${diffInDays} dias`;
  };

  if (loading) {
    return <DefaultLoader title="Dashboard" messages={['Carregando dados do dashboard...', 'Processando estatísticas...', 'Quase pronto...']} />;
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: 'var(--color-red)'
      }}>
        <div>{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ 
          fontSize: 'var(--font-size-24)', 
          fontWeight: 600, 
          color: 'var(--color-dimgray)', 
          margin: '0 0 8px 0' 
        }}>
          Bem-vindo, {user?.username}!
        </h2>
        <p style={{ 
          fontSize: 'var(--font-size-16)', 
          color: 'var(--color-darkgray)', 
          margin: 0 
        }}>
          Aqui está o resumo das atividades do sistema.
        </p>
      </div>

      {(role?.admin || role?.gestor) && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: showFilters ? '20px' : '0'
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-18)',
              fontWeight: 600,
              color: 'var(--color-dimgray)',
              margin: 0
            }}>
              Filtros de Receita
            </h3>
            <button
              className="btn btn-secondary"
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px'
              }}
            >
              <Filter size={16} />
              {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
            </button>
          </div>

          {showFilters && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              padding: '20px',
              backgroundColor: 'var(--color-lightgray)',
              borderRadius: 'var(--br-8)',
              marginTop: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-14)',
                  fontWeight: 500,
                  color: 'var(--color-dimgray)',
                  marginBottom: '8px'
                }}>
                  Data Inicial
                </label>
                <input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => handleFilterChange({ startDate: e.target.value })}
                  className="input"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-14)',
                  fontWeight: 500,
                  color: 'var(--color-dimgray)',
                  marginBottom: '8px'
                }}>
                  Data Final
                </label>
                <input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => handleFilterChange({ endDate: e.target.value })}
                  className="input"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-14)',
                  fontWeight: 500,
                  color: 'var(--color-dimgray)',
                  marginBottom: '8px'
                }}>
                  Status
                </label>
                <select
                  value={filters.status || 'completed'}
                  onChange={(e) => handleFilterChange({ status: e.target.value })}
                  className="input"
                  style={{ width: '100%' }}
                >
                  <option value="completed">Concluído</option>
                  <option value="all">Todos</option>
                  <option value="pending">Pendente</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'end' }}>
                <button
                  className="btn btn-primary"
                  onClick={fetchRevenueData}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Calendar size={16} />
                  Aplicar Filtros
                </button>
              </div>
            </div>
          )}

          {revenueStats && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: 'var(--br-8)',
              borderLeft: '4px solid var(--color-green)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ 
                    fontSize: 'var(--font-size-16)', 
                    color: 'var(--color-dimgray)', 
                    margin: '0 0 4px 0' 
                  }}>
                    Receita do Período Selecionado
                  </h4>
                  <p style={{ 
                    fontSize: 'var(--font-size-24)', 
                    fontWeight: 700, 
                    color: 'var(--color-green)', 
                    margin: 0 
                  }}>
                    {formatCurrency(revenueStats.totalRevenue)}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ 
                    fontSize: 'var(--font-size-14)', 
                    color: 'var(--color-darkgray)', 
                    margin: '0 0 4px 0' 
                  }}>
                    {revenueStats.period}
                  </p>
                  <p style={{ 
                    fontSize: 'var(--font-size-14)', 
                    color: 'var(--color-darkgray)', 
                    margin: 0 
                  }}>
                    {revenueStats.count} transações
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filtros de Receita — Ônibus */}
      {(role?.admin || role?.gestor) && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: showBusFilters ? '20px' : '0'
          }}>
            <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, color: 'var(--color-dimgray)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bus size={18} />
              Filtros de Receita — Ônibus
            </h3>
            <button
              className="btn btn-secondary"
              onClick={() => setShowBusFilters(!showBusFilters)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px' }}
            >
              <Filter size={16} />
              {showBusFilters ? 'Ocultar' : 'Mostrar'} Filtros
            </button>
          </div>

          {showBusFilters && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              padding: '20px',
              backgroundColor: 'var(--color-lightgray)',
              borderRadius: 'var(--br-8)',
              marginTop: '16px',
            }}>
              <div>
                <label style={{ display: 'block', fontSize: 'var(--font-size-14)', fontWeight: 500, color: 'var(--color-dimgray)', marginBottom: '8px' }}>
                  Data Inicial
                </label>
                <input
                  type="date"
                  value={busFilters.startDate || ''}
                  onChange={(e) => handleBusFilterChange({ startDate: e.target.value })}
                  className="input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 'var(--font-size-14)', fontWeight: 500, color: 'var(--color-dimgray)', marginBottom: '8px' }}>
                  Data Final
                </label>
                <input
                  type="date"
                  value={busFilters.endDate || ''}
                  onChange={(e) => handleBusFilterChange({ endDate: e.target.value })}
                  className="input"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'end' }}>
                <button
                  className="btn btn-primary"
                  onClick={fetchBusRevenueData}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  <Calendar size={16} />
                  Aplicar Filtros
                </button>
              </div>
            </div>
          )}

          {busRevenueStats && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#eff6ff',
              borderRadius: 'var(--br-8)',
              borderLeft: '4px solid var(--color-blue)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ fontSize: 'var(--font-size-16)', color: 'var(--color-dimgray)', margin: '0 0 4px 0' }}>
                    Receita de Ônibus no Período
                  </h4>
                  <p style={{ fontSize: 'var(--font-size-24)', fontWeight: 700, color: 'var(--color-blue)', margin: 0 }}>
                    {formatCurrency(busRevenueStats.totalRevenue)}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: 'var(--font-size-14)', color: 'var(--color-darkgray)', margin: '0 0 4px 0' }}>
                    {busRevenueStats.period}
                  </p>
                  <p style={{ fontSize: 'var(--font-size-14)', color: 'var(--color-darkgray)', margin: 0 }}>
                    {busRevenueStats.count} transações
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {getVisibleStats().map((stat, index) => (
          <div key={index} className="card" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: 'var(--br-8)',
              backgroundColor: `${getColorClass(stat.color)}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: getColorClass(stat.color)
            }}>
              {stat.icon}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: 'var(--font-size-32)',
                fontWeight: 700,
                color: 'var(--color-dimgray)',
                margin: '0 0 4px 0'
              }}>
                {stat.value}
              </h3>
              <p style={{
                fontSize: 'var(--font-size-14)',
                color: 'var(--color-darkgray)',
                margin: 0
              }}>
                {stat.title}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 style={{
          fontSize: 'var(--font-size-18)',
          fontWeight: 600,
          color: 'var(--color-dimgray)',
          margin: '0 0 20px 0'
        }}>
          Atividades Recentes
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {dashboardData?.recentActivities && dashboardData.recentActivities.length > 0 ? (
            dashboardData.recentActivities.map((activity) => (
              <div key={activity.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 0',
                borderBottom: '1px solid var(--color-gray)'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: getColorClass(activity.color)
                }}></div>
                <span style={{ fontSize: 'var(--font-size-14)' }}>
                  {activity.message}
                </span>
                <span style={{ 
                  fontSize: 'var(--font-size-12)', 
                  color: 'var(--color-darkgray)',
                  marginLeft: 'auto'
                }}>
                  {formatTimeAgo(new Date(activity.timestamp))}
                </span>
              </div>
            ))
          ) : (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: 'var(--color-darkgray)',
              fontSize: 'var(--font-size-14)'
            }}>
              Nenhuma atividade recente encontrada.
            </div>
          )}
        </div>
      </div>

      {role?.admin && (
        <div className="card" style={{ marginTop: '32px' }}>
          <h3 style={{
            fontSize: 'var(--font-size-18)',
            fontWeight: 600,
            color: 'var(--color-dimgray)',
            margin: '0 0 20px 0'
          }}>
            Ações Rápidas
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate('/users')}
              style={{
                padding: '16px',
                textAlign: 'left'
              }}
            >
              <Users size={20} />
              Gerenciar Usuários
            </button>
            
            <button 
              className="btn btn-secondary" 
              onClick={() => navigate('/markups')}
              style={{
                padding: '16px',
                textAlign: 'left'
              }}
            >
              <TrendingUp size={20} />
              Configurar Markups
            </button>
            
            <button 
              className="btn btn-secondary" 
              onClick={() => navigate('/emissions')}
              style={{
                padding: '16px',
                textAlign: 'left'
              }}
            >
              <CreditCard size={20} />
              Ver Emissões
            </button>
            
            <button 
              className="btn btn-secondary" 
              onClick={() => navigate('/settings')}
              style={{
                padding: '16px',
                textAlign: 'left'
              }}
            >
              <SettingsIcon size={20} />
              Configurações
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;